import * as React from 'react'
import * as LucideIcons from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/**
 * A curated set of icons commonly used for work item types.
 * Each entry is a PascalCase key matching the lucide-react export.
 */
const ICON_OPTIONS = [
  'Square', 'Circle', 'Triangle', 'Diamond', 'Hexagon', 'Octagon', 'Star', 'Heart',
  'Bug', 'Zap', 'Flame', 'Rocket', 'Target', 'Flag', 'Bookmark', 'Shield',
  'AlertTriangle', 'AlertCircle', 'Info', 'HelpCircle', 'CheckCircle', 'XCircle',
  'Clock', 'Calendar', 'FileText', 'File', 'Folder', 'Database', 'Server',
  'Code', 'Terminal', 'GitBranch', 'GitPullRequest', 'GitMerge',
  'Wrench', 'Settings', 'Cog', 'Tool', 'Hammer', 'Puzzle',
  'Eye', 'Search', 'Filter', 'Layers', 'Layout', 'Grid',
  'ArrowUp', 'ArrowRight', 'TrendingUp', 'BarChart', 'PieChart',
  'Users', 'User', 'UserCheck', 'MessageSquare', 'MessageCircle',
  'Lightbulb', 'Award', 'Crown', 'Trophy', 'Medal',
  'Package', 'Box', 'Archive', 'Inbox', 'Send',
  'Link', 'Paperclip', 'Tag', 'Hash', 'AtSign',
  'Play', 'Pause', 'SkipForward', 'RefreshCw', 'RotateCcw',
] as const

interface IconPickerInlineProps {
  value: string
  onChange: (iconName: string) => void
}

export function IconPickerInline({ value, onChange }: IconPickerInlineProps) {
  const [search, setSearch] = React.useState('')

  const filteredIcons = React.useMemo(() => {
    if (!search.trim()) return ICON_OPTIONS as unknown as Array<string>
    const term = search.toLowerCase()
    return (ICON_OPTIONS as unknown as Array<string>).filter((name) =>
      name.toLowerCase().includes(term),
    )
  }, [search])

  return (
    <div className="space-y-2">
      <Input
        placeholder="Search icons..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 text-sm"
      />
      <div className="grid grid-cols-8 gap-1 max-h-[160px] overflow-auto rounded-md border border-input bg-background p-2">
        {filteredIcons.length === 0 ? (
          <div className="col-span-8 py-4 text-center text-sm text-muted-foreground">
            No icons found
          </div>
        ) : (
          filteredIcons.map((iconName) => {
            const Icon = (LucideIcons as unknown as Record<string, React.FC<LucideIcons.LucideProps>>)[iconName]
            if (!Icon) return null
            const isSelected = value === iconName
            return (
              <button
                key={iconName}
                type="button"
                onClick={() => onChange(iconName)}
                className={cn(
                  'flex items-center justify-center rounded-md p-1.5 transition-colors',
                  isSelected
                    ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
                title={iconName}
              >
                <Icon className="size-4" />
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
